import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/* =========================================================================
   ARCHITECTURE TOPOLOGY METADATA (100% Vector Native Engine)
   ========================================================================= */

const NODE_DETAILS = {
  apis: {
    id: "apis",
    title: "APIs",
    category: "INTERFACES",
    tagline: "High-concurrency RESTful and gRPC microservice interfaces.",
    tech: "OpenAPI 3.0, gRPC, OAuth 2.0",
    status: "Active",
  },
  web: {
    id: "web",
    title: "Web",
    category: "CLIENT TIER",
    tagline: "Responsive web apps and client dashboards.",
    tech: "React, TypeScript, Next.js",
    status: "Active",
  },
  mobile: {
    id: "mobile",
    title: "Mobile",
    category: "CLIENT TIER",
    tagline: "Cross-platform iOS and Android native apps.",
    tech: "Flutter, Dart, SQLite, Push APIs",
    status: "Active",
  },
  cloud: {
    id: "cloud",
    title: "Cloud",
    category: "NETWORK",
    tagline: "Dedicated bare metal and edge networks.",
    tech: "Bare Metal, Edge CDN, TLS 1.3",
    status: "Active",
  },
  data: {
    id: "data",
    title: "Data",
    category: "PERSISTENCE",
    tagline: "Relational persistence and distributed caching.",
    tech: "PostgreSQL 16, Redis 7, S3",
    status: "Active",
  },
  security: {
    id: "security",
    title: "Security",
    category: "GOVERNANCE",
    tagline: "Zero-trust network defense and data encryption.",
    tech: "AES-256, Zero-Trust, RBAC",
    status: "Active",
  },
  applications: {
    id: "applications",
    title: "Applications",
    category: "PRESENTATION TIER",
    tagline: "Customer and enterprise presentation tier.",
    tech: "Next.js SSR, React 18, Tailwind",
    status: "Active",
  },
  services: {
    id: "services",
    title: "Services",
    category: "CORE RUNTIME",
    tagline: "Scalable backend systems and business logic.",
    tech: "NestJS, Python FastAPI, Go",
    status: "Active",
  },
  infrastructure: {
    id: "infrastructure",
    title: "Infrastructure",
    category: "SYSTEM TIER",
    tagline: "Container clusters and autonomous SRE.",
    tech: "Docker, Kubernetes, Prometheus",
    status: "Active",
  },
};

export function SoftwareHeroArchitecture({ onSelectNode }) {
  const prefersReducedMotion = useReducedMotion();
  const [activeId, setActiveId] = useState("apis");
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const activeNode = NODE_DETAILS[activeId] || NODE_DETAILS.apis;

  const handleMouseMove = (e) => {
    if (prefersReducedMotion || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 12;
    setMouseOffset({ x, y });
  };

  const handleMouseLeave = () => {
    setMouseOffset({ x: 0, y: 0 });
  };

  const handleSelect = (id) => {
    setActiveId(id);
    if (onSelectNode) onSelectNode(id);
  };

  /* Exact Mathematical 3D Isometric Center and Spacing */
  const slabCX = 365;
  const topSlabY = 195;
  const midSlabY = 312;
  const botSlabY = 428;

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-full aspect-[760/620] max-w-[620px] lg:max-w-[660px] xl:max-w-[720px] mx-auto select-none"
      style={{
        transform: prefersReducedMotion
          ? "none"
          : `translate3d(${mouseOffset.x}px, ${mouseOffset.y}px, 0)`,
        transition: "transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
      }}
      role="region"
      aria-label="Interactive Software Architecture Diagram"
    >
      {/* 100% Native Vector SVG Diagram (Zero Images, Zero Screen Shots, Crisp HD at Any DPI) */}
      <svg
        viewBox="0 0 760 620"
        className="w-full h-full overflow-visible"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Subtle Ambient Radial Mask for Fading Background Grid with NO Edges */}
          <radialGradient id="grid-fade" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.8" />
            <stop offset="85%" stopColor="#FFFFFF" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </radialGradient>
          <mask id="grid-mask">
            <rect width="760" height="620" fill="url(#grid-fade)" />
          </mask>

          {/* Clay Material: Top Face */}
          <linearGradient id="clay-top" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#F5F1E9" />
          </linearGradient>

          {/* Clay Material: Left Front Face (Shadow side) */}
          <linearGradient id="clay-left" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E2DCD1" />
            <stop offset="100%" stopColor="#D4CEBF" />
          </linearGradient>

          {/* Clay Material: Right Front Face */}
          <linearGradient id="clay-right" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EDE7DC" />
            <stop offset="100%" stopColor="#DFD8CA" />
          </linearGradient>

          {/* Obsidian Material: Services Top Face */}
          <linearGradient id="obsidian-top" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#252429" />
            <stop offset="100%" stopColor="#141316" />
          </linearGradient>

          {/* Obsidian Material: Left Front Face */}
          <linearGradient id="obsidian-left" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1C1B1F" />
            <stop offset="100%" stopColor="#0F0E11" />
          </linearGradient>

          {/* Obsidian Material: Right Front Face */}
          <linearGradient id="obsidian-right" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#28262C" />
            <stop offset="100%" stopColor="#161518" />
          </linearGradient>

          {/* Ambient Radiant Orange Core Glow */}
          <radialGradient id="orange-core-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF5A1F" stopOpacity="0.45" />
            <stop offset="45%" stopColor="#FF5A1F" stopOpacity="0.18" />
            <stop offset="85%" stopColor="#FF5A1F" stopOpacity="0.03" />
            <stop offset="100%" stopColor="#FF5A1F" stopOpacity="0" />
          </radialGradient>

          {/* Subtle Ground Shadows */}
          <filter id="shadow-blur-sm" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="8" />
          </filter>
          <filter id="shadow-blur-lg" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="16" />
          </filter>
          <filter id="orange-blur" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="12" />
          </filter>

          {/* Chip Floating Shadows */}
          <filter id="chip-shadow" x="-40%" y="-30%" width="180%" height="180%">
            <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#000000" floodOpacity="0.06" />
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000000" floodOpacity="0.04" />
          </filter>
          <filter id="chip-shadow-active" x="-40%" y="-30%" width="180%" height="180%">
            <feDropShadow dx="0" dy="12" stdDeviation="14" floodColor="#FF5A1F" floodOpacity="0.22" />
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000000" floodOpacity="0.08" />
          </filter>

          {/* Neon Glow Filter */}
          <filter id="neon-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* -------------------------------------------------------------------
            1. FAINT ISOMETRIC GRID LINES (Zero Borders, Fades Gracefully to #FAF8F5)
            ------------------------------------------------------------------- */}
        <g mask="url(#grid-mask)" stroke="#0B0B0B" strokeWidth="0.75" strokeOpacity="0.04">
          {[-200, -120, -40, 40, 120, 200, 280, 360, 440, 520, 600, 680, 760, 840, 920].map(
            (c, i) => (
              <line
                key={`iso-1-${i}`}
                x1={c}
                y1={0}
                x2={c + 760}
                y2={438}
              />
            )
          )}
          {[-200, -120, -40, 40, 120, 200, 280, 360, 440, 520, 600, 680, 760, 840, 920].map(
            (c, i) => (
              <line
                key={`iso-2-${i}`}
                x1={c}
                y1={620}
                x2={c + 760}
                y2={182}
              />
            )
          )}
        </g>

        {/* -------------------------------------------------------------------
            2. AMBIENT RADIANT CORE HALO BEHIND SERVICES SLAB
            ------------------------------------------------------------------- */}
        <ellipse
          cx={slabCX}
          cy={midSlabY + 12}
          rx="180"
          ry="90"
          fill="url(#orange-core-glow)"
          pointerEvents="none"
        />

        {/* -------------------------------------------------------------------
            3. HARDWARE CIRCUIT TRACES ROUTING TO CENTRAL SERVICES CORE
            ------------------------------------------------------------------- */}
        <g strokeLinecap="round" strokeLinejoin="round">
          {/* Path: Web -> Services */}
          <path
            id="trace-web"
            d={`M 180,185 L 255,230 L 295,260 L 305,285`}
            stroke="#FF5A1F"
            strokeWidth={activeId === "web" ? "2.2" : "1.4"}
            strokeOpacity={activeId === "web" ? "0.95" : "0.55"}
          />
          {/* Path: Mobile -> Services */}
          <path
            id="trace-mobile"
            d={`M 145,325 L 215,325 L 260,320 L 295,312`}
            stroke="#FF5A1F"
            strokeWidth={activeId === "mobile" ? "2.2" : "1.4"}
            strokeOpacity={activeId === "mobile" ? "0.95" : "0.55"}
          />
          {/* Path: Cloud -> Services */}
          <path
            id="trace-cloud"
            d={`M 195,470 L 255,430 L 295,385 L 310,340`}
            stroke="#FF5A1F"
            strokeWidth={activeId === "cloud" ? "2.2" : "1.4"}
            strokeOpacity={activeId === "cloud" ? "0.95" : "0.55"}
          />
          {/* Path: APIs / Telemetry Card -> Services */}
          <path
            id="trace-apis"
            d={`M 525,200 L 485,235 L 445,268 L 425,285`}
            stroke="#FF5A1F"
            strokeWidth={activeId === "apis" ? "2.2" : "1.4"}
            strokeOpacity={activeId === "apis" ? "0.95" : "0.55"}
          />
          {/* Path: Data -> Services */}
          <path
            id="trace-data"
            d={`M 615,330 L 530,330 L 475,320 L 435,312`}
            stroke="#FF5A1F"
            strokeWidth={activeId === "data" ? "2.2" : "1.4"}
            strokeOpacity={activeId === "data" ? "0.95" : "0.55"}
          />
          {/* Path: Security -> Services */}
          <path
            id="trace-security"
            d={`M 575,470 L 495,430 L 450,385 L 420,340`}
            stroke="#FF5A1F"
            strokeWidth={activeId === "security" ? "2.2" : "1.4"}
            strokeOpacity={activeId === "security" ? "0.95" : "0.55"}
          />

          {/* Circuit Junction Dots */}
          <circle cx="255" cy="230" r="3.2" fill="#FF5A1F" />
          <circle cx="295" cy="260" r="2.8" fill="#FF5A1F" />
          <circle cx="215" cy="325" r="3.2" fill="#FF5A1F" />
          <circle cx="255" cy="430" r="3.2" fill="#FF5A1F" />
          <circle cx="295" cy="385" r="2.8" fill="#FF5A1F" />
          <circle cx="485" cy="235" r="3.2" fill="#FF5A1F" />
          <circle cx="445" cy="268" r="2.8" fill="#FF5A1F" />
          <circle cx="530" cy="330" r="3.2" fill="#FF5A1F" />
          <circle cx="495" cy="430" r="3.2" fill="#FF5A1F" />
          <circle cx="450" cy="385" r="2.8" fill="#FF5A1F" />

          {/* Animated Hardware Data Pulses Traveling Along Traces */}
          {!prefersReducedMotion && (
            <>
              <circle r="2.8" fill="#FFFFFF" stroke="#FF5A1F" strokeWidth="1.2" filter="url(#neon-glow)">
                <animateMotion dur="2.8s" repeatCount="indefinite">
                  <mpath href="#trace-web" />
                </animateMotion>
              </circle>
              <circle r="2.8" fill="#FFFFFF" stroke="#FF5A1F" strokeWidth="1.2" filter="url(#neon-glow)">
                <animateMotion dur="3.2s" repeatCount="indefinite">
                  <mpath href="#trace-mobile" />
                </animateMotion>
              </circle>
              <circle r="2.8" fill="#FFFFFF" stroke="#FF5A1F" strokeWidth="1.2" filter="url(#neon-glow)">
                <animateMotion dur="3.6s" repeatCount="indefinite">
                  <mpath href="#trace-cloud" />
                </animateMotion>
              </circle>
              <circle r="2.8" fill="#FFFFFF" stroke="#FF5A1F" strokeWidth="1.2" filter="url(#neon-glow)">
                <animateMotion dur="2.6s" repeatCount="indefinite">
                  <mpath href="#trace-apis" />
                </animateMotion>
              </circle>
              <circle r="2.8" fill="#FFFFFF" stroke="#FF5A1F" strokeWidth="1.2" filter="url(#neon-glow)">
                <animateMotion dur="3.4s" repeatCount="indefinite">
                  <mpath href="#trace-data" />
                </animateMotion>
              </circle>
              <circle r="2.8" fill="#FFFFFF" stroke="#FF5A1F" strokeWidth="1.2" filter="url(#neon-glow)">
                <animateMotion dur="3.0s" repeatCount="indefinite">
                  <mpath href="#trace-security" />
                </animateMotion>
              </circle>
            </>
          )}
        </g>

        {/* -------------------------------------------------------------------
            4. TIER 3: BOTTOM SLAB (INFRASTRUCTURE - Crisp Clay/White)
            ------------------------------------------------------------------- */}
        <g
          transform={`translate(${slabCX}, ${botSlabY})`}
          className="cursor-pointer group"
          onClick={() => handleSelect("infrastructure")}
          onMouseEnter={() => setActiveId("infrastructure")}
        >
          {/* Ambient Ground Shadow on Platform */}
          <ellipse
            cx="0"
            cy="76"
            rx="98"
            ry="28"
            fill="#000000"
            fillOpacity="0.08"
            filter="url(#shadow-blur-lg)"
          />

          {/* Left-Front Extrusion Face */}
          <path
            d="M -108,6 C -96,32 -55,54 0,54 L 0,72 C -55,72 -96,50 -108,24 Z"
            fill="url(#clay-left)"
            stroke="#D0C9BB"
            strokeWidth="0.5"
          />

          {/* Right-Front Extrusion Face */}
          <path
            d="M 0,54 C 55,54 96,32 108,6 L 108,24 C 96,50 55,72 0,72 Z"
            fill="url(#clay-right)"
            stroke="#D0C9BB"
            strokeWidth="0.5"
          />

          {/* Front Center Bevel Crease */}
          <line x1="0" y1="54" x2="0" y2="72" stroke="#BEB7A8" strokeWidth="0.8" />

          {/* Top Face */}
          <path
            d="M 0,-54 C 55,-54 96,-32 108,-6 C 112,-2 112,2 108,6 C 96,32 55,54 0,54 C -55,54 -96,32 -108,6 C -112,2 -112,-2 -108,-6 C -96,-32 -55,-54 0,-54 Z"
            fill="url(#clay-top)"
            stroke="rgba(255,255,255,0.95)"
            strokeWidth="1.2"
          />

          {/* Subtle Chamfer Highlight Rim */}
          <path
            d="M -106,6 C -95,31 -54,52 0,52 C 54,52 95,31 106,6"
            fill="none"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth="1"
          />

          {/* Infrastructure Tier Icon & Typography */}
          <g transform="translate(0, 0)">
            <g transform="translate(-52, -11)" stroke="#0B0B0B" strokeWidth="1.5" fill="none">
              <ellipse cx="7" cy="4" rx="7" ry="2.5" />
              <path d="M 0,4 v 4 c 0,1.4 3.1,2.5 7,2.5 c 3.9,0 7,-1.1 7,-2.5 v -4" />
              <path d="M 0,8 v 4 c 0,1.4 3.1,2.5 7,2.5 c 3.9,0 7,-1.1 7,-2.5 v -4" />
            </g>
            <text
              x="-30"
              y="2"
              fill="#0B0B0B"
              fontSize="12.5"
              fontWeight="600"
              fontFamily="Space Grotesk, sans-serif"
              letterSpacing="0.02em"
            >
              Infrastructure
            </text>
          </g>
        </g>

        {/* -------------------------------------------------------------------
            5. TIER 2: MIDDLE SLAB (SERVICES - Signature Obsidian & Glowing Orange Rim)
            ------------------------------------------------------------------- */}
        <g
          transform={`translate(${slabCX}, ${midSlabY})`}
          className="cursor-pointer group"
          onClick={() => handleSelect("services")}
          onMouseEnter={() => setActiveId("services")}
        >
          {/* Intense Orange Light Spill Beneath Obsidian Slab */}
          <ellipse
            cx="0"
            cy="74"
            rx="110"
            ry="30"
            fill="#FF5A1F"
            fillOpacity="0.32"
            filter="url(#orange-blur)"
          />

          {/* Left-Front Extrusion Face */}
          <path
            d="M -108,6 C -96,32 -55,54 0,54 L 0,72 C -55,72 -96,50 -108,24 Z"
            fill="url(#obsidian-left)"
            stroke="#FF5A1F"
            strokeWidth="0.5"
            strokeOpacity="0.4"
          />

          {/* Right-Front Extrusion Face */}
          <path
            d="M 0,54 C 55,54 96,32 108,6 L 108,24 C 96,50 55,72 0,72 Z"
            fill="url(#obsidian-right)"
            stroke="#FF5A1F"
            strokeWidth="0.5"
            strokeOpacity="0.4"
          />

          {/* Luminous Orange Bottom Perimeter Glow Rail */}
          <path
            d="M -108,24 C -96,50 -55,72 0,72 C 55,72 96,50 108,24"
            fill="none"
            stroke="#FF5A1F"
            strokeWidth="2.2"
            filter="url(#neon-glow)"
          />

          {/* Front Center Bevel Crease */}
          <line x1="0" y1="54" x2="0" y2="72" stroke="#FF5A1F" strokeWidth="1" strokeOpacity="0.8" />

          {/* Top Face: Deep Obsidian Carbon */}
          <path
            d="M 0,-54 C 55,-54 96,-32 108,-6 C 112,-2 112,2 108,6 C 96,32 55,54 0,54 C -55,54 -96,32 -108,6 C -112,2 -112,-2 -108,-6 C -96,-32 -55,-54 0,-54 Z"
            fill="url(#obsidian-top)"
            stroke="#FF5A1F"
            strokeWidth="1.6"
            filter="url(#neon-glow)"
          />

          {/* Inner Neon Inset Trace Line on Services Top Face */}
          <path
            d="M 0,-42 C 43,-42 75,-25 84,-4 C 88,-1 88,1 84,4 C 75,25 43,42 0,42 C -43,42 -75,25 -84,4 C -88,1 -88,-1 -84,-4 C -75,-25 -43,-42 0,-42 Z"
            fill="none"
            stroke="#FF5A1F"
            strokeWidth="0.8"
            strokeOpacity="0.5"
          />

          {/* Services Tier Icon & Typography */}
          <g transform="translate(0, 0)">
            <g transform="translate(-36, -11)" stroke="#FF5A1F" strokeWidth="1.5" fill="none">
              <path d="M 6,0 L 12,3.5 L 6,7 L 0,3.5 Z" fill="rgba(255,90,31,0.2)" />
              <path d="M 0,3.5 v 6 L 6,13 v -6 Z" />
              <path d="M 12,3.5 v 6 L 6,13 v -6 Z" />
            </g>
            <text
              x="-16"
              y="2"
              fill="#FFFFFF"
              fontSize="12.5"
              fontWeight="600"
              fontFamily="Space Grotesk, sans-serif"
              letterSpacing="0.02em"
            >
              Services
            </text>
          </g>

          {/* Left and Right Bus Sockets in Luminous Orange */}
          <circle cx="-106" cy="6" r="3.5" fill="#FF5A1F" filter="url(#neon-glow)" />
          <circle cx="106" cy="6" r="3.5" fill="#FF5A1F" filter="url(#neon-glow)" />
        </g>

        {/* -------------------------------------------------------------------
            6. TIER 1: TOP SLAB (APPLICATIONS - Crisp Clay/White)
            ------------------------------------------------------------------- */}
        <g
          transform={`translate(${slabCX}, ${topSlabY})`}
          className="cursor-pointer group"
          onClick={() => handleSelect("applications")}
          onMouseEnter={() => setActiveId("applications")}
        >
          {/* Ambient Shadow Cast Downward toward Services */}
          <ellipse
            cx="0"
            cy="76"
            rx="96"
            ry="26"
            fill="#000000"
            fillOpacity="0.08"
            filter="url(#shadow-blur-sm)"
          />

          {/* Left-Front Extrusion Face */}
          <path
            d="M -108,6 C -96,32 -55,54 0,54 L 0,72 C -55,72 -96,50 -108,24 Z"
            fill="url(#clay-left)"
            stroke="#D0C9BB"
            strokeWidth="0.5"
          />

          {/* Right-Front Extrusion Face */}
          <path
            d="M 0,54 C 55,54 96,32 108,6 L 108,24 C 96,50 55,72 0,72 Z"
            fill="url(#clay-right)"
            stroke="#D0C9BB"
            strokeWidth="0.5"
          />

          {/* Front Center Bevel Crease */}
          <line x1="0" y1="54" x2="0" y2="72" stroke="#BEB7A8" strokeWidth="0.8" />

          {/* Top Face */}
          <path
            d="M 0,-54 C 55,-54 96,-32 108,-6 C 112,-2 112,2 108,6 C 96,32 55,54 0,54 C -55,54 -96,32 -108,6 C -112,2 -112,-2 -108,-6 C -96,-32 -55,-54 0,-54 Z"
            fill="url(#clay-top)"
            stroke="rgba(255,255,255,0.95)"
            strokeWidth="1.2"
          />

          {/* Subtle Chamfer Highlight Rim */}
          <path
            d="M -106,6 C -95,31 -54,52 0,52 C 54,52 95,31 106,6"
            fill="none"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth="1"
          />

          {/* Applications Tier Icon & Typography */}
          <g transform="translate(0, 0)">
            <g transform="translate(-48, -10)" stroke="#0B0B0B" strokeWidth="1.5" fill="none">
              <rect x="0" y="0" width="14" height="11" rx="2" />
              <line x1="0" y1="3.5" x2="14" y2="3.5" />
              <circle cx="3" cy="1.8" r="0.6" fill="#0B0B0B" />
              <circle cx="5" cy="1.8" r="0.6" fill="#0B0B0B" />
            </g>
            <text
              x="-26"
              y="2"
              fill="#0B0B0B"
              fontSize="12.5"
              fontWeight="600"
              fontFamily="Space Grotesk, sans-serif"
              letterSpacing="0.02em"
            >
              Applications
            </text>
          </g>
        </g>

        {/* -------------------------------------------------------------------
            7. SURROUNDING FLOATING NODES (Web, Mobile, Cloud, Data, Security)
            ------------------------------------------------------------------- */}

        {/* NODE: WEB (Top-Left) */}
        <g
          transform="translate(150, 180)"
          className="cursor-pointer group"
          onClick={() => handleSelect("web")}
          onMouseEnter={() => setActiveId("web")}
        >
          <rect
            x="-30"
            y="-32"
            width="60"
            height="64"
            rx="16"
            fill="#FFFFFF"
            stroke={activeId === "web" ? "#FF5A1F" : "#E9E5DC"}
            strokeWidth={activeId === "web" ? "2" : "1.2"}
            filter={activeId === "web" ? "url(#chip-shadow-active)" : "url(#chip-shadow)"}
          />
          <g transform="translate(-10, -18)" stroke={activeId === "web" ? "#FF5A1F" : "#0B0B0B"} strokeWidth="1.5" fill="none">
            <rect x="2" y="1" width="16" height="11" rx="2" />
            <line x1="0" y1="15" x2="20" y2="15" strokeLinecap="round" />
            <line x1="7" y1="12" x2="13" y2="12" />
          </g>
          <text
            x="0"
            y="17"
            textAnchor="middle"
            fill="#0B0B0B"
            fontSize="10.5"
            fontWeight="600"
            fontFamily="Inter, sans-serif"
          >
            Web
          </text>
        </g>

        {/* NODE: MOBILE (Mid-Left) */}
        <g
          transform="translate(115, 325)"
          className="cursor-pointer group"
          onClick={() => handleSelect("mobile")}
          onMouseEnter={() => setActiveId("mobile")}
        >
          <rect
            x="-30"
            y="-32"
            width="60"
            height="64"
            rx="16"
            fill="#FFFFFF"
            stroke={activeId === "mobile" ? "#FF5A1F" : "#E9E5DC"}
            strokeWidth={activeId === "mobile" ? "2" : "1.2"}
            filter={activeId === "mobile" ? "url(#chip-shadow-active)" : "url(#chip-shadow)"}
          />
          <g transform="translate(-8, -18)" stroke={activeId === "mobile" ? "#FF5A1F" : "#0B0B0B"} strokeWidth="1.5" fill="none">
            <rect x="1" y="0" width="14" height="18" rx="3" />
            <line x1="6" y1="14" x2="10" y2="14" strokeLinecap="round" />
          </g>
          <text
            x="0"
            y="17"
            textAnchor="middle"
            fill="#0B0B0B"
            fontSize="10.5"
            fontWeight="600"
            fontFamily="Inter, sans-serif"
          >
            Mobile
          </text>
        </g>

        {/* NODE: CLOUD (Bot-Left) */}
        <g
          transform="translate(165, 470)"
          className="cursor-pointer group"
          onClick={() => handleSelect("cloud")}
          onMouseEnter={() => setActiveId("cloud")}
        >
          <rect
            x="-30"
            y="-32"
            width="60"
            height="64"
            rx="16"
            fill="#FFFFFF"
            stroke={activeId === "cloud" ? "#FF5A1F" : "#E9E5DC"}
            strokeWidth={activeId === "cloud" ? "2" : "1.2"}
            filter={activeId === "cloud" ? "url(#chip-shadow-active)" : "url(#chip-shadow)"}
          />
          <g transform="translate(-10, -17)" stroke={activeId === "cloud" ? "#FF5A1F" : "#0B0B0B"} strokeWidth="1.5" fill="none">
            <path d="M 5,14 a 4,4 0 0,1 -1,-7.8 a 6,6 0 0,1 11.6,-1.2 a 4.5,4.5 0 0,1 4.4,4.5 a 4.5,4.5 0 0,1 -4.5,4.5 z" />
          </g>
          <text
            x="0"
            y="17"
            textAnchor="middle"
            fill="#0B0B0B"
            fontSize="10.5"
            fontWeight="600"
            fontFamily="Inter, sans-serif"
          >
            Cloud
          </text>
        </g>

        {/* NODE: DATA (Mid-Right) */}
        <g
          transform="translate(645, 330)"
          className="cursor-pointer group"
          onClick={() => handleSelect("data")}
          onMouseEnter={() => setActiveId("data")}
        >
          <rect
            x="-30"
            y="-32"
            width="60"
            height="64"
            rx="16"
            fill="#FFFFFF"
            stroke={activeId === "data" ? "#FF5A1F" : "#E9E5DC"}
            strokeWidth={activeId === "data" ? "2" : "1.2"}
            filter={activeId === "data" ? "url(#chip-shadow-active)" : "url(#chip-shadow)"}
          />
          <g transform="translate(-8, -18)" stroke={activeId === "data" ? "#FF5A1F" : "#0B0B0B"} strokeWidth="1.5" fill="none">
            <ellipse cx="8" cy="4" rx="8" ry="3" />
            <path d="M 0,4 v 5 c 0,1.7 3.6,3 8,3 c 4.4,0 8,-1.3 8,-3 v -5" />
            <path d="M 0,9 v 5 c 0,1.7 3.6,3 8,3 c 4.4,0 8,-1.3 8,-3 v -5" />
          </g>
          <text
            x="0"
            y="17"
            textAnchor="middle"
            fill="#0B0B0B"
            fontSize="10.5"
            fontWeight="600"
            fontFamily="Inter, sans-serif"
          >
            Data
          </text>
        </g>

        {/* NODE: SECURITY (Bot-Right) */}
        <g
          transform="translate(605, 470)"
          className="cursor-pointer group"
          onClick={() => handleSelect("security")}
          onMouseEnter={() => setActiveId("security")}
        >
          <rect
            x="-30"
            y="-32"
            width="60"
            height="64"
            rx="16"
            fill="#FFFFFF"
            stroke={activeId === "security" ? "#FF5A1F" : "#E9E5DC"}
            strokeWidth={activeId === "security" ? "2" : "1.2"}
            filter={activeId === "security" ? "url(#chip-shadow-active)" : "url(#chip-shadow)"}
          />
          <g transform="translate(-8, -18)" stroke={activeId === "security" ? "#FF5A1F" : "#0B0B0B"} strokeWidth="1.5" fill="none">
            <path d="M 8,1 L 1,4 v 5 c 0,5.5 3.5,8.8 7,10 c 3.5,-1.2 7,-4.5 7,-10 v -5 Z" />
            <path d="M 5,9.5 L 7,11.5 L 11,7.5" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <text
            x="0"
            y="17"
            textAnchor="middle"
            fill="#0B0B0B"
            fontSize="10.5"
            fontWeight="600"
            fontFamily="Inter, sans-serif"
          >
            Security
          </text>
        </g>
      </svg>

      {/* -------------------------------------------------------------------
          8. SLEEK DOCKED TELEMETRY CARD (Docked Gracefully at Top-Right)
          ------------------------------------------------------------------- */}
      <div
        className="absolute z-20 pointer-events-auto cursor-pointer"
        onClick={() => handleSelect("apis")}
        style={{
          left: "63%",
          top: "8%",
          width: "35%",
          maxWidth: "265px",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeNode.id}
            initial={{ opacity: 0, y: 4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="rounded-2xl bg-[#141416] text-white p-3.5 sm:p-4 shadow-2xl border border-white/10 backdrop-blur-md"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm font-bold text-white font-display">
                  {activeNode.title}
                </span>
                <span className="text-[9px] font-mono tracking-wider text-neutral-400 uppercase">
                  {activeNode.category}
                </span>
              </div>
              <ArrowUpRight className="w-3.5 h-3.5 text-[#FF5A1F] flex-shrink-0" />
            </div>

            <p className="text-[10px] sm:text-[11px] text-neutral-300 leading-snug font-sans mb-2.5">
              {activeNode.tagline}
            </p>

            <div className="text-[9px] sm:text-[10px] font-mono text-neutral-400 pt-2 border-t border-white/10 flex items-center justify-between">
              <span className="truncate">{activeNode.tech}</span>
              <span className="text-[#FF5A1F] font-semibold flex items-center gap-1 ml-2 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                {activeNode.status}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
